import { isMacOS } from "../../../../utils";
export enum AlignmentType {
  left = "left",
  right = "right",
  justify = "justify",
}

const AlignmentMenuActions = {
  title: "PROPERTIES_PANEL.ALIGNMENT",
  actionsList: [
    {
      label: "PROPERTIES_PANEL.LEFT",
      value: AlignmentType.left,
      key: "alignment",
    },
    {
      label: "PROPERTIES_PANEL.JUSTIFY",
      value: AlignmentType.justify,
      key: "alignment",
    },
    {
      label: "PROPERTIES_PANEL.RIGHT",
      value: AlignmentType.right,
      key: "alignment",
    },
  ],
};

export enum PlanVersion {
  v3_0_0 = "3.0.0",
  v3_1_0 = "3.1.0",
}

export enum ObjectType {
  Grid = "grid",
  Door = "door",
  Shelf = "shelf",
  Allocation = "allocation",
  Product = "product",
  Text = "text",
  Bin = "bin",
  Obstruction = "obstruction",
  Sign = "sign",
  Chest = "chest",
  PolygonalShelf = "polygonal_shelf",
  SpaceBlock = "space_block",
}

export const FIXTURE_TYPES = [
  ObjectType.Shelf,
  ObjectType.Chest,
  ObjectType.PolygonalShelf,
  ObjectType.Bin,
];

export enum ObjectAction {
  ADD = "add",
  DELETE = "delete",
  UPDATE = "update",
}

export const ObjectRenderOrder: { [key: string]: number } = {
  grid: 9999,
  door: 8888,
  shelf: 7777,
  chest: 7777,
  polygonal_shelf: 7777,
  obstruction: 7777,
  bin: 6666,
  sign: 6666,
  allocation: 5555,
  space_block: 5555,
  product: 4444,
  text: 1111,
};

export const selectionRenderOrder: { [key: string]: number } = {
  grid: 9999,
  door: 8888,
  shelf: 4444,
  chest: 4444,
  polygonal_shelf: 4444,
  obstruction: 7777,
  bin: 4444,
  sign: 6666,
  allocation: 5555,
  space_block: 5555,
  product: 4444,
  text: 1111,
};

export const selectionOrder = Object.keys(selectionRenderOrder).sort(
  (a: string, b: string) => {
    return selectionRenderOrder[a] - selectionRenderOrder[b];
  }
);

export const ObjectDefaultColor = {
  shelf: "#808080",
  chest: "#bbb100",
  bin: "#607d8b",
  polygonal_shelf: "#808080",
  obstruction: "#333333",
  sign: "#ffeb3b",
  base: "#000000",
  space_block: "#F9EDC6",
};

function getCommonMenuActions(type: ObjectType) {
  return [
    {
      label: "EDITOR.COPY",
      type,
      action: "copy",
    },
    {
      label: "EDITOR.PASTE",
      type,
      action: "paste",
      disabled: true,
      actions: [
        {
          label: "EDITOR.PASTE_ALL",
          type,
          action: "paste",
        },
        {
          label: "EDITOR.PASTE_EXCLUDE_SKUs",
          type,
          action: "pasteNoSku",
        },
      ],
    },
  ];
}

export const DoorContextMenu = [
  ...getCommonMenuActions(ObjectType.Door),
  {
    label: "EDITOR.ADD_DOOR",
    type: ObjectType.Door,
    action: "add",
  },
  {
    label: "EDITOR.REMOVE_DOOR",
    type: ObjectType.Door,
    action: "delete",
  },
];

export const ShelfContextMenu = [
  {
    label: "EDITOR.ADD_SHELF",
    type: ObjectType.Shelf,
    action: "add",
    disabled: false,
  },
  {
    label: "EDITOR.REMOVE_SHELF",
    type: ObjectType.Shelf,
    action: "delete",
    disabled: false,
  },
];

export const DefaultContextMenu = [
  {
    label: "Delete",
    type: ObjectType.Shelf,
    action: "delete",
  },
];

export const VirtualLabelContextMenu = [
  {
    label: "Color",
    icon: "edit-dropdown.svg",
    type: ObjectType.Text,
    action: "color_palette_drop",
  },
  {
    label: "Add Label",
    icon: "edit-plus.svg",
    type: ObjectType.Text,
    action: "add",
    toolTipText: "BASE_GRID.ADD",
  },
  {
    label: "Edit Label",
    icon: "edit-change.svg",
    type: ObjectType.Text,
    action: "edit",
    toolTipText: "BASE_GRID.EDIT",
  },
  {
    label: "Copy",
    icon: "icon-copy-white.svg",
    type: ObjectType.Text,
    action: "copy",
    toolTipText: "BASE_GRID.COPY",
  },
  {
    label: "Paste",
    icon: "icon-clipboard-white.svg",
    type: ObjectType.Text,
    action: "paste",
    toolTipText: "BASE_GRID.PASTE",
    disabled: true,
  },
  {
    label: "Remove Label",
    icon: "edit-delete.svg",
    type: ObjectType.Text,
    action: "delete",
    toolTipText: "BASE_GRID.DELETE",
  },
];

export const DefaultToolbarMenu = [
  {
    label: "Color",
    icon: "edit-dropdown.svg",
    type: ObjectType.Text,
    action: "color_palette_drop",
  },
  {
    label: "Remove",
    icon: "edit-delete.svg",
    type: ObjectType.Text,
    action: "delete",
    toolTipText: "BASE_GRID.DELETE",
  },
];

export const OtherObjectContextMenu = {
  type: ObjectType.Chest,
  alignmentMenuActions: AlignmentMenuActions,
  copyPasteMenuActions: getCommonMenuActions(ObjectType.Chest),
  addDeleteMenuActions: [
    {
      label: "EDITOR.REMOVE_CHEST",
      type: ObjectType.Chest,
      action: "delete",
      disabled: false,
    },
  ],
};

export enum ProductFacing {
  Front = "front",
  Top = "top",
  Side = "side",
}

export enum ProductBeforeAfter {
  before = "before",
  after = "after",
}

export enum GroupUngroup {
  Group = "group",
  Ungroup = "ungroup",
}

export enum ProductRotation {
  zero = "zero",
  ninety = "ninety",
}

const OrientationMenuActions = {
  title: "MY_PLANO.ORIENTATION",
  actionsList: [
    {
      label: "MY_PLANO.ORIENTATION_FRONT",
      value: ProductFacing.Front,
      key: "face",
    },
    {
      label: "MY_PLANO.ORIENTATION_TOP",
      value: ProductFacing.Top,
      key: "face",
    },
    {
      label: "MY_PLANO.ORIENTATION_SIDE",
      value: ProductFacing.Side,
      key: "face",
    },
  ],
  rotateMenuAction: {
    label: "MY_PLANO.ROTATE",
    value: ProductRotation.zero,
    key: "rotation",
  },
};

const FacingsMenuActions = {
  title: "MY_PLANO.FACING",
  actionsList: [
    {
      label: "MY_PLANO.HORIZONTAL_FACING",
      key: "facings",
      facingKey: "x",
      value: "",
      min: 1,
      max: 150,
    },
    {
      label: "MY_PLANO.VERTICAL_FACING",
      key: "facings",
      facingKey: "y",
      value: "",
      min: 1,
      max: 20,
    },
  ],
};

const AddProductBeforeAndAfterMenuActions = {
  title: "MY_PLANO.ADD",
  actionsList: [
    {
      label: "MY_PLANO.ADD_BEFORE",
      value: ProductBeforeAfter.before,
      key: "add",
    },
    {
      label: "MY_PLANO.ORIENTATION_TOP",
      value: ProductBeforeAfter.after,
      key: "add",
    },
  ],
};

const GroupUnGroupActions = [
  {
    label: "MY_PLANO.GROUP",
    type: ObjectType.Shelf,
    action: GroupUngroup.Group,
    disabled: true,
  },
  {
    label: "MY_PLANO.UNGROUP",
    type: ObjectType.Shelf,
    action: GroupUngroup.Ungroup,
    disabled: false,
  },
];

export const ProductPropertiesMenu = {
  type: ObjectType.Product,
  orientationMenuActions: OrientationMenuActions,
  groupUnGroupActions: GroupUnGroupActions,
  addProductBeforeAndAfterMenuActions: AddProductBeforeAndAfterMenuActions,
  facingsMenuActions: FacingsMenuActions,
  copyPasteMenuActions: getCommonMenuActions(ObjectType.Product),
  skuConfigAction: {
    label: "MY_PLANO.SKU_CONFIG",
    value: "default",
    key: "sku_config",
    action: "update",
    selectOptions: [],
  },
  deleteActions: {
    label: "MY_PLANO.DELETE_SKU",
    value: "delete",
    action: "delete",
  },
};

export const ShelfPropertiesMenu = {
  type: ObjectType.Shelf,
  copyPasteMenuActions: getCommonMenuActions(ObjectType.Shelf),
  alignmentMenuActions: AlignmentMenuActions,
  addDeleteMenuActions: ShelfContextMenu,
  groupUnGroupActions: GroupUnGroupActions,
};

let position: { [key: string]: any } = {
  x: 0,
  y: 0,
  z: 0,
  angle: 0,
};

export const EmptyObject = {
  id: "",
  parent_id: "",
  label: "",
  plan_id: "",
  type: "",
  position: position,
  position3d: position,
  width: 0,
  height: 0,
  depth: 0,
  properties: {
    alignment: "",
    can_combine: null,
  },
};

export enum ControlPanel {
  Undo = "undo",
  Redo = "redo",
  AddDoor = "add-door",
  TwoD = "twoD",
  ThreeD = "threeD",
  SystemRecommend = "system-recommend",
  Dashboard = "plano-dashboard",
}

export enum MouseClick {
  IS_LEFT_MOUSE_BUTTON = 0,
  IS_RIGHT_MOUSE_BUTTON = 2,
}

export enum SocketMessageType {
  SAVE_CHANGE = "save_change",
  GET_CHANGE = "get_change",
  BROADCAST = "broadcast",
  GET_NEXT_CHANGE = "get_next_change",
  SYNC = "sync",
}

export const KeyboardShortcutsKey = [
  {
    header: "Select SKU",
    shortcuts: [
      {
        label: "Random SKU Selection",
        keys: ["", "", "", "", isMacOS() ? "Cmd" : "Ctrl", "SKU"],
      },
      {
        label: "Sequential SKU Selection",
        keys: ["", "", "", "", "Shift", "SKU"],
      },
      {
        label: "Select all SKU",
        keys: ["", "", "", "", isMacOS() ? "Cmd" : "Ctrl", "A"],
      },
    ],
  },
  {
    header: "Zoom",
    shortcuts: [
      {
        label: "Zoom In",
        keys: ["", "", "", "", isMacOS() ? "Cmd" : "Ctrl", "+"],
      },
      {
        label: "Zoom Out",
        keys: ["", "", "", "", isMacOS() ? "Cmd" : "Ctrl", "-"],
      },
      {
        label: "Reset Zoom",
        keys: ["", "", "", "", isMacOS() ? "Cmd" : "Ctrl", "0"],
      },
    ],
  },
  {
    header: "Orientation & Rotation",
    shortcuts: [
      { label: "Front Facing", keys: ["", "", "", "", "", "F"] },
      { label: "Top Facing", keys: ["", "", "", "", "", "T"] },
      { label: "SideFacing", keys: ["", "", "", "", "", "S"] },
      { label: "Rotate", keys: ["", "", "", "", "", "R"] },
    ],
  },
  {
    header: "Undo & Redo",
    shortcuts: [
      {
        label: "Undo",
        keys: ["", "", "", "", isMacOS() ? "Cmd" : "Ctrl", "Z"],
      },
      {
        label: "Redo",
        keys: [
          isMacOS() ? "Cmd" : "Ctrl",
          "Y",
          "or",
          isMacOS() ? "Cmd" : "Ctrl",
          "Shift",
          "Y",
        ],
      },
    ],
  },
  {
    header: "Set Facing of the SKU",
    shortcuts: [
      { label: "Set Horizontal Facing", keys: ["", "", "1 to 9 Keys"] },
      {
        label: "Set Vertical Facing",
        keys: ["", isMacOS() ? "Cmd" : "Ctrl", "1 to 9 Keys"],
      },
    ],
  },
  {
    header: "Door Labels",
    shortcuts: [
      {
        label: "Add Door Labels",
        keys: ["", "", "", isMacOS() ? "Cmd" : "Ctrl", "Shift", "V"],
      },
    ],
  },
];

export enum Mirroring {
  TRAFFIC_FLOW = "traffic_flow",
  DOORS_SHELVES = "doors_shelves",
  DOORS_SHELVES_PRODUCTS = "doors_shelves_products",
  VIRTUAL_DOOR_LABELS = "virtual_labels",
}

export enum DoorHandle {
  LEFT = "left",
  RIGHT = "right",
  NONE = "none",
}

export const DOOR_HANDLE = {
  WIDTH: 1,
  HEIGHT: 5,
  DEPTH: 1,
};

export const BITMAP_LIMIT = 3;

export enum BitmapOptions {
  SKU_DETAIL = "sku_detail",
  DOS = "dos_multiplier",
  PACK_TYPE = "pack_type",
  PACK_QTY = "pack_quantity",
  SEGMENT = "segment",
  BRAND = "brand",
  UNITS = "units",
  IMAGE = "image",
  CLEAR_ALL = "clear_all",
}

export const PRODUCT_LIBRARY_DETAILS: any = {
  tableList: [
    {
      label: "",
      value: "check_box",
      valueType: "",
      filterType: "",
      inputType: "checkbox",
      // frozenColumn: true,
      sortable: false,
    },
    {
      label: "",
      value: "sku_image",
      valueType: "",
      filterType: "",
      // frozenColumn: true,
      inputType: "sku_image",
    },
    {
      label: "SKU ID",
      value: "sku",
      sortable: false,
      // frozenColumn: true,
      alignFrozen: "left",
      filterType: "input_text",
      filterMatchMode: "contains",
    },
    {
      label: "SKU Name",
      value: "sku_detail",
      sortable: false,
      // frozenColumn: true,
      alignFrozen: "left",
      filterType: "input_text",
      filterMatchMode: "contains",
      trimText: true,
    },
    {
      label: "Segment",
      value: "segment",
      sortable: false,
      filterType: "input_text",
      filterMatchMode: "contains",
      trimText: true,
    },
    {
      label: "Style",
      value: "style",
      sortable: false,
      filterType: "input_text",
      filterMatchMode: "contains",
      trimText: true,
    },
    {
      label: "Manufacturer",
      value: "manufacturer",
      sortable: false,
      filterType: "input_text",
      filterMatchMode: "contains",
      trimText: true,
    },
    {
      label: "Brand",
      value: "brand",
      sortable: false,
      filterType: "input_text",
      filterMatchMode: "contains",
      trimText: true,
    },
    {
      label: "Category",
      value: "category",
      sortable: false,
      filterType: "input_text",
      filterMatchMode: "contains",
      trimText: true,
    },
    {
      label: "Pack Type",
      value: "pack_type",
      sortable: false,
      filterType: "input_text",
      filterMatchMode: "contains",
      colMinWidth: true,
    },
    {
      label: "Pack Size",
      value: "pack_size",
      sortable: false,
      filterType: "input_text",
      filterMatchMode: "contains",
      colMinWidth: true,
    },
    {
      label: "Pack Qty",
      value: "pack_quantity",
      sortable: false,
      filterType: "input_text",
      filterMatchMode: "contains",
      colMinWidth: true,
    },
    // {
    //   label: "Length",
    //   value: "width",
    //   sortable: false,
    //   filterType: "input_text",
    //   filterMatchMode: "contains",
    //   colMinWidth: true,
    // },
    // {
    //   label: "Height",
    //   value: "height",
    //   sortable: false,
    //   filterType: "input_text",
    //   filterMatchMode: "contains",
    //   colMinWidth: true,
    // },
    // {
    //   label: "Depth",
    //   value: "depth",
    //   sortable: false,
    //   filterType: "input_text",
    //   filterMatchMode: "contains",
    //   colMinWidth: true,
    // },
    {
      label: "Action",
      value: "action",
      valueType: "icon-view",
      // frozenColumn: true,
      colMinWidth: true,
      class: "flex justify-center items-center gap-2",
      actionList: [
        {
          label: "Add to Exp",
          key: "add",
          icon: "icon-add",
          color: "#000000",
          disableKey: "hasAdded",
          enableKey: true,
          isLoadingKey: "isLoadingFlag",
        },
        {
          label: "Sync to Sku",
          key: "sync",
          icon: "icon-sync-sku",
          color: "#E5B611",
          enableKey: "sync_to_sku",
          disableKey: "hasSynced",
          isLoadingKey: "isLoading",
        },
      ],
    },
  ],
};

export const confirmDialogContent = {
  head: "RESET_PLANOGRAM.RESET",
  description: "",
  btnCancel: "GRID.CANCEL",
  btnProceed: "GRID.CONTINUE",
  showConfirmDialog: false,
  styleClass: "confirm-dialog",
  dialogkey: "reset",
};

export const planMenuActionList = [
  { label: "GRID.RENAME", action: "rename" },
  { label: "GRID.DELETE", action: "delete" },
];

export const planRenameDialogContent = {
  head: "GRID.RENAME",
  label: "GRID.PLAN_NAME",
  showPlanRenameDialog: false,
  selectedPlan: [],
  formField: {
    label: "GRID.PLAN_NAME",
    model: "name",
    placeholder: "PLANOGRAM_INFO.ENTER_PLANOGRAM_NAME",
  },
  btnCancel: "GRID.CANCEL",
  btnProceed: "GRID.PROCEED",
};

export const confirmDialogContentPlanDelete = {
  head: "GRID.DELETE_PLANOGRAM",
  description: "",
  selectedPlan: [],
  btnCancel: "GRID.CANCEL",
  btnProceed: "GRID.DELETE",
  showConfirmDialog: false,
  styleClass: "confirm-dialog",
  dialogkey: "delete",
};

export const validationErrorObjects = {
  shelfOverflow: {
    type: "error",
    count: 0,
    errorLabel: "Overflowing Shelfs",
    errors: [],
  },
  shelfStacking: {
    type: "error",
    count: 0,
    errorLabel: "Shelf stacking",
    errors: [],
  },
  shelfLabelDuplicate: {
    type: "warning",
    count: 0,
    errorLabel: "Duplicate Shelf Labels",
    errors: [],
  },
  doorLabelDuplicate: {
    type: "warning",
    count: 0,
    errorLabel: "Duplicate Door Labels",
    errors: [],
  },
  floatingFixtures: {
    type: "error",
    count: 0,
    errorLabel: "Floating Fixtures",
    errors: [],
  },
  productOverflow: {
    type: "warning",
    count: 0,
    errorLabel: "SKU Overflow",
    errors: [],
  },
  noImageProduct: {
    type: "warning",
    count: 0,
    errorLabel: "SKUs without image",
    errors: [],
  },
};

export const spaceBlockTemplateContent = {
  showSpaceBlockDialog: false,
  selectedSpaceBlock: {},
  loadTemplate: true,
};

export enum ConnectionStatus {
  CONNECTED = "connected",
  NOT_CONNECTED = "not-connected",
  RECONNECTING = "reconnecting",
}

export const fieldsToSyncProductKeys = [
  "width",
  "height",
  "depth",
  "sku",
  "sku_detail",
  "brand",
  "category",
  "manufacturer",
  "pack_quantity",
  "pack_size",
  "pack_type",
  "segment",
  "style",
  "image_json",
  "additional_info",
  "version",
];

export const variantsKey = ["tray", "case", "display", "alternate", "loose"];
